import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import { returnUrlFromSearchParams } from '@klear/ui/wizard/return-url'

const DEFAULT_TITLE = 'Discard unsaved changes?'
const DEFAULT_DESCRIPTION = 'You have unsaved changes. Leave and discard them?'
const RETURN_URL_DIRTY_DESCRIPTION =
  'You have unsaved changes on this wizard. Leave and discard them? You will return to the previous screen without saving this progress.'

export type UseWizardDirtyLeaveGuardOptions = {
  when: boolean
  title?: string
  description?: string
}

export type UseWizardDirtyLeaveGuardResult = {
  LeaveConfirmDialog: ReactNode
  guardExit: (exit: () => void) => () => void
}

export function shouldConfirmWizardExit(whenDirty: boolean): boolean {
  return whenDirty
}

export function useWizardDirtyLeaveGuard(options: UseWizardDirtyLeaveGuardOptions): UseWizardDirtyLeaveGuardResult {
  const { when, title: titleOverride, description: descriptionOverride } = options
  const [searchParams] = useSearchParams()
  const hasReturnUrl = returnUrlFromSearchParams(searchParams) != null
  const shouldConfirmExit = shouldConfirmWizardExit(when)

  const title = titleOverride ?? DEFAULT_TITLE
  let description = descriptionOverride ?? DEFAULT_DESCRIPTION
  if (descriptionOverride == null && when && hasReturnUrl) {
    description = RETURN_URL_DIRTY_DESCRIPTION
  }

  const { confirm, ConfirmDialog } = useConfirmDialog()
  const shouldConfirmRef = useRef(shouldConfirmExit)
  shouldConfirmRef.current = shouldConfirmExit
  const promptRef = useRef({ title, description })
  promptRef.current = { title, description }

  const guardExit = useCallback(
    (exit: () => void) => {
      return () => {
        if (!shouldConfirmRef.current) {
          exit()
          return
        }
        const { title: t, description: d } = promptRef.current
        confirm({
          title: t,
          description: d,
          confirmLabel: 'Leave',
          onConfirm: exit
        })
      }
    },
    [confirm]
  )

  useEffect(() => {
    if (!when) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [when])

  return { LeaveConfirmDialog: ConfirmDialog, guardExit }
}
